import type {
  ExternalToolResult,
  TerraUIMessage,
} from '@terra/shared-dto-chat-types';
import type {
  BatchDeclarationGate,
  ExternalToolBatch,
  ExternalToolResultCoordinator,
  LostExecuteFire,
  NestedToolCallIdSets,
  NestedToolCallTimeouts,
  ToolCallExpectedGates,
} from './external-tool-batch.types';
import { declareExternalToolBatchAliases } from './external-tool-batch-declare';
import {
  aliasExternalToolBatch,
  getLiveBatchAliasId,
  getOrCreateExternalToolBatch,
  getWorkToken,
  incrementWorkToken,
  isBatchWorkCurrent,
  isStaleCompleteRequest,
} from './external-tool-batch-gates';
import {
  findKnownChatBatch,
  prepareExternalToolBatchOpen,
  resolveWritableExecuteBatch,
} from './external-tool-batch-open';
import {
  awaitBatchDeclarationGate,
  awaitToolCallExpectedGate,
  isBatchDeclarationAborted,
  resetExternalToolChatBatches,
} from './external-tool-batch-waiters';
import {
  applyCachedResults,
  clearCompletedExternalToolResultsForChat,
  deliverOrphanExternalToolResult,
  globalCompletedExternalToolResultsByBatchAndToolCallId,
  rememberCompletedExternalToolResult,
  reportBackgroundFlushError,
} from './external-tool-result-cache';
import { createExternalToolFlushController } from './external-tool-result-flush';
import {
  armLostExecuteForMissingExecutes,
  armLostExecuteForWaitingChatBatches,
  clearLostExecuteForToolCall,
  collectArrivedExecuteToolCallIds,
  holdMissingDeclaredExecutes,
  isIgnoredExecute,
  rememberStoppedToolCallIds,
  resolveCompleteRequestBatch,
  syncLostExecuteForBatch,
  takeLostExecuteResult,
} from './external-tool-lost-execute';

export type {
  ExternalToolRequest,
  ExternalToolResultCoordinator,
  OpenExternalToolBatchParams,
} from './external-tool-batch.types';
export type { ExternalToolResult };
export {
  clearCompletedExternalToolResultsCache,
  getExternalToolResultForRetry,
} from './external-tool-result-cache';

export function createExternalToolResultCoordinator({
  sendMessage,
  onChatActivityChange,
}: {
  sendMessage: (chatId: string, message: TerraUIMessage) => Promise<void>;
  onChatActivityChange?: (params: { chatId: string; active: boolean }) => void;
}): ExternalToolResultCoordinator {
  const batches = new Map<string, ExternalToolBatch>();
  const inFlightFlushes = new Map<string, Promise<void>>();
  const inFlightHandlerCounts = new Map<string, number>();
  const batchDeclarationGates = new Map<string, BatchDeclarationGate>();
  const toolCallExpectedGates: ToolCallExpectedGates = new Map();
  const lostExecuteTimers: NestedToolCallTimeouts = new Map();
  const ignoredExecuteToolCallIds: NestedToolCallIdSets = new Map();
  const priorFlushByChatId = new Map<string, Promise<void>>();
  const lastReportedActivityByChatId = new Map<string, boolean>();
  const workTokenByChatId = new Map<string, number>();
  const handlerWorkTokenByBatchId = new Map<string, number>();
  const stoppedToolCallIds = new Map<string, Set<string>>();

  const getOrCreateBatch = ({
    batchId,
    chatId,
  }: {
    batchId: string;
    chatId: string;
  }): ExternalToolBatch =>
    getOrCreateExternalToolBatch({
      batches,
      workTokenByChatId,
      batchId,
      chatId,
    });

  const lostExecuteSink: { fire: LostExecuteFire } = { fire: () => undefined };

  const {
    recomputeChatActivity,
    tryFlushBatch,
    notifyHandlerStart,
    notifyHandlerEnd,
    awaitPriorFlush,
  } = createExternalToolFlushController({
    batches,
    inFlightFlushes,
    inFlightHandlerCounts,
    priorFlushByChatId,
    lastReportedActivityByChatId,
    workTokenByChatId,
    handlerWorkTokenByBatchId,
    sendMessage,
    onChatActivityChange,
    lostExecuteTimers,
    batchDeclarationGates,
    toolCallExpectedGates,
    onFlushSettled: ({ chatId, skipBatch }) => {
      armLostExecuteForWaitingChatBatches({
        batches,
        inFlightFlushes,
        skipBatch,
        chatId,
        lostExecuteTimers,
        ignoredExecuteToolCallIds,
        onLost: (params) => lostExecuteSink.fire(params),
      });
    },
  });

  const resetChatBatches = (chatId: string): void => {
    incrementWorkToken({ workTokenByChatId, chatId });
    rememberStoppedToolCallIds({ stoppedToolCallIds, batches, chatId });
    resetExternalToolChatBatches({
      chatId,
      batches,
      batchDeclarationGates,
      toolCallExpectedGates,
      lostExecuteTimers,
      ignoredExecuteToolCallIds,
      inFlightFlushes,
      inFlightHandlerCounts,
      handlerWorkTokenByBatchId,
    });
    clearCompletedExternalToolResultsForChat(chatId);
    recomputeChatActivity(chatId);
  };

  const completeRequest = async (result: ExternalToolResult): Promise<void> => {
    if (
      isBatchDeclarationAborted({
        gates: batchDeclarationGates,
        batchId: result.batchId,
      }) ||
      (isIgnoredExecute({
        batches,
        ignoredExecuteToolCallIds,
        batchId: result.batchId,
        toolCallId: result.toolCallId,
      }) &&
        (batches.get(result.batchId)?.results.has(result.toolCallId) ?? true))
    ) {
      return;
    }

    if (
      isStaleCompleteRequest({
        batches,
        handlerWorkTokenByBatchId,
        workTokenByChatId,
        batchId: result.batchId,
        chatId: result.chatId,
      })
    ) {
      return;
    }

    const batch = resolveCompleteRequestBatch({
      batches,
      handlerWorkTokenByBatchId,
      workTokenByChatId,
      batchId: result.batchId,
      chatId: result.chatId,
      toolCallId: result.toolCallId,
    });

    if (!batch) {
      if (stoppedToolCallIds.get(result.chatId)?.has(result.toolCallId)) {
        return;
      }

      rememberCompletedExternalToolResult(result);

      if (getWorkToken(workTokenByChatId, result.chatId) !== 0) {
        return;
      }

      const chatHasBatch = [...batches.values()].some(
        (candidate) => candidate.chatId === result.chatId,
      );

      if (chatHasBatch) {
        return;
      }

      await deliverOrphanExternalToolResult({ sendMessage, result });

      return;
    }

    rememberCompletedExternalToolResult(result);

    if (!batch.requests.has(result.toolCallId)) {
      batch.requests.set(result.toolCallId, {
        batchId: result.batchId,
        chatId: result.chatId,
        toolCallId: result.toolCallId,
        toolName: result.toolName,
      });
    }

    batch.results.set(result.toolCallId, result);
    batch.pendingExecuteToolCallIds.delete(result.toolCallId);
    const flushBatchId =
      getLiveBatchAliasId({ batches, batch }) ?? result.batchId;

    clearLostExecuteForToolCall({
      batches,
      lostExecuteTimers,
      batchId: flushBatchId,
      toolCallId: result.toolCallId,
    });
    recomputeChatActivity(result.chatId);
    await tryFlushBatch(flushBatchId);
  };

  const fireLostExecute = ({
    batchId,
    toolCallId,
    toolName,
  }: {
    batchId: string;
    toolCallId: string;
    toolName: string;
  }): void => {
    const result = takeLostExecuteResult({
      batches,
      workTokenByChatId,
      ignoredExecuteToolCallIds,
      batchId,
      toolCallId,
      toolName,
    });

    if (!result) {
      return;
    }

    void completeRequest(result).catch(reportBackgroundFlushError);
  };

  lostExecuteSink.fire = fireLostExecute;

  return {
    openBatch: ({
      batchId,
      chatId,
      resumeGroupId,
      toolCallIds,
      toolName,
      toolNameByToolCallId,
    }) => {
      const prepared = prepareExternalToolBatchOpen({
        batches,
        batchDeclarationGates,
        inFlightFlushes,
        batchId,
        chatId,
        resumeGroupId,
        toolCallIds,
      });

      if (prepared.status === 'ignored') {
        return;
      }

      const { aliasIds, existingBatch, deferLostExecute } = prepared;
      const canonicalId = resumeGroupId ?? batchId;
      const batch =
        existingBatch ?? getOrCreateBatch({ batchId: canonicalId, chatId });

      aliasExternalToolBatch({
        batches,
        batch,
        aliasIds,
        incomingBatchId: batchId,
      });

      if (!isBatchWorkCurrent({ batch, workTokenByChatId })) {
        return;
      }

      for (const toolCallId of toolCallIds) {
        batch.toolNameByToolCallId.set(
          toolCallId,
          toolNameByToolCallId?.[toolCallId] ??
            batch.toolNameByToolCallId.get(toolCallId) ??
            toolName,
        );
      }

      const arrivedToolCallIds = collectArrivedExecuteToolCallIds({
        batch,
        toolCallIds,
      });
      const declaredToolCallIds = declareExternalToolBatchAliases({
        batch,
        aliasIds,
        toolCallIds,
        toolCallExpectedGates,
        batchDeclarationGates,
      });

      if (deferLostExecute) {
        holdMissingDeclaredExecutes({
          batch,
          toolCallIds: declaredToolCallIds,
          arrivedToolCallIds,
        });
      } else {
        armLostExecuteForMissingExecutes({
          batches,
          lostExecuteTimers,
          ignoredExecuteToolCallIds,
          batch,
          batchId: canonicalId,
          toolCallIds: declaredToolCallIds,
          arrivedToolCallIds,
          toolName,
          onLost: (params) => lostExecuteSink.fire(params),
        });
      }

      for (const aliasId of aliasIds) {
        applyCachedResults({
          batch,
          batchId: aliasId,
          completedResultsByToolCallId:
            globalCompletedExternalToolResultsByBatchAndToolCallId,
        });
      }

      syncLostExecuteForBatch({
        batches,
        lostExecuteTimers,
        batchId: canonicalId,
      });
      recomputeChatActivity(chatId);
      void tryFlushBatch(canonicalId).catch(reportBackgroundFlushError);
    },

    awaitToolCallExpected: ({ batchId, toolCallId }) => {
      if (
        isIgnoredExecute({
          batches,
          ignoredExecuteToolCallIds,
          batchId,
          toolCallId,
        })
      ) {
        return Promise.resolve('timedOut');
      }

      const declarationStatus = batchDeclarationGates.get(batchId)?.status;

      if (declarationStatus === 'timedOut' || declarationStatus === 'aborted') {
        return Promise.resolve(declarationStatus);
      }

      const expectedStatus = toolCallExpectedGates
        .get(batchId)
        ?.get(toolCallId)?.status;

      if (expectedStatus && expectedStatus !== 'pending') {
        return Promise.resolve(expectedStatus);
      }

      const batch = batches.get(batchId);

      if (batch?.expectedToolCallIds.has(toolCallId)) {
        return Promise.resolve('expected');
      }

      return awaitToolCallExpectedGate({
        gates: toolCallExpectedGates,
        batchId,
        toolCallId,
      });
    },

    awaitBatchDeclaration: (batchId) => {
      const existingStatus = batchDeclarationGates.get(batchId)?.status;

      if (existingStatus && existingStatus !== 'pending') {
        return Promise.resolve(existingStatus);
      }

      const batch = batches.get(batchId);

      if (batch?.declared) {
        return Promise.resolve('declared');
      }

      return awaitBatchDeclarationGate({
        gates: batchDeclarationGates,
        batchId,
      });
    },

    awaitPriorFlush,

    markExecutePending: ({ batchId, chatId, toolCallId }) => {
      if (
        isIgnoredExecute({
          batches,
          ignoredExecuteToolCallIds,
          batchId,
          toolCallId,
        }) ||
        isBatchDeclarationAborted({
          gates: batchDeclarationGates,
          batchId,
        })
      ) {
        return;
      }

      clearLostExecuteForToolCall({
        batches,
        lostExecuteTimers,
        batchId,
        toolCallId,
      });
      const batch = resolveWritableExecuteBatch({
        batches,
        workTokenByChatId,
        batchId,
        chatId,
        toolCallId,
      });

      if (!batch) {
        return;
      }

      batch.pendingExecuteToolCallIds.add(toolCallId);
      recomputeChatActivity(batch.chatId);
    },

    clearExecutePending: ({ batchId, chatId, toolCallId }) => {
      const batch =
        (chatId === undefined
          ? undefined
          : findKnownChatBatch({
              batches,
              workTokenByChatId,
              chatId,
              toolCallId,
            })) ?? batches.get(batchId);

      if (!batch) {
        return;
      }

      batch.pendingExecuteToolCallIds.delete(toolCallId);
      recomputeChatActivity(batch.chatId);
      void tryFlushBatch(
        getLiveBatchAliasId({ batches, batch }) ?? batchId,
      ).catch(reportBackgroundFlushError);
    },

    registerRequest: (request) => {
      if (
        isIgnoredExecute({
          batches,
          ignoredExecuteToolCallIds,
          batchId: request.batchId,
          toolCallId: request.toolCallId,
        }) ||
        isBatchDeclarationAborted({
          gates: batchDeclarationGates,
          batchId: request.batchId,
        })
      ) {
        return;
      }

      const batch = resolveWritableExecuteBatch({
        batches,
        workTokenByChatId,
        batchId: request.batchId,
        chatId: request.chatId,
        toolCallId: request.toolCallId,
      });

      if (!batch) {
        return;
      }

      clearLostExecuteForToolCall({
        batches,
        lostExecuteTimers,
        batchId: getLiveBatchAliasId({ batches, batch }) ?? request.batchId,
        toolCallId: request.toolCallId,
      });
      batch.requests.set(request.toolCallId, request);
      if (batch.expectedToolCallIds.has(request.toolCallId)) {
        batch.pendingExecuteToolCallIds.delete(request.toolCallId);
      }

      applyCachedResults({
        batch,
        batchId: request.batchId,
        completedResultsByToolCallId:
          globalCompletedExternalToolResultsByBatchAndToolCallId,
      });

      recomputeChatActivity(request.chatId);
      void tryFlushBatch(
        getLiveBatchAliasId({ batches, batch }) ?? request.batchId,
      ).catch(reportBackgroundFlushError);
    },

    notifyHandlerStart,
    notifyHandlerEnd,
    completeRequest,
    resetChatBatches,
  };
}

let boundResetExternalToolChatBatches: ((chatId: string) => void) | null = null;

export function bindExternalToolResultCoordinatorReset({
  resetChatBatches,
}: {
  resetChatBatches: (chatId: string) => void;
}): void {
  boundResetExternalToolChatBatches = resetChatBatches;
}

export function resetExternalToolWorkForChat(chatId: string): void {
  boundResetExternalToolChatBatches?.(chatId);
}
